import { useState } from "react";
import { TableColumn } from "react-data-table-component";
import { Table } from "containers/Table";
import { IColum } from "containers/Table/interfaces";
import { PrimaryButton } from "components/Button/style";
import {
  FaColumns,
  FaDollarSign,
  FaDownload,
  FaFileInvoiceDollar,
  FaMoneyCheckAlt,
  FaSpinner,
} from "react-icons/fa";
import { converterData, converterDataHoraBr } from "_utils/Data";
import { formatarDinheiro } from "_utils/Money";
import { utils, writeFileXLSX } from "xlsx";
import { toastErro } from "components/Toast";
import { IDebitoIpva } from "../../interfaces/IDebitoIpva";
import { IFiltroDebitoIpva } from "../../interfaces/IFiltroDebitoIpva";
import { StatusDebito } from "../StatusDebito";
import { StatusProcesso } from "../StatusProcesso";
import { IconeErroDebito } from "../IconeErroDebito";
import { ModalConfigColunas } from "../ModalConfigColunas";
import {
  defaultConfigColunasAguardando,
  defaultConfigColunasPago,
} from "../../constants/defaultConfigColunas";
import { Container, Header, HeaderContainerBotoes, TextoHeader } from "./style";
import { StatusPagamento } from "features/core/pages/Financeiro/Enum/StatusPagamento";
import { LoadingCirculo } from "components/LoadingCirculo";

const LOCAL_STORAGE_KEY: Record<"aguardando" | "pago", string> = {
  aguardando: "config_pagar_ipva_colunas_aguardando",
  pago: "config_pagar_ipva_colunas_pago",
};

interface IProps {
  modo: "aguardando" | "pago";
  debitos: IDebitoIpva[];
  loading: boolean;
  totalRows: number;
  limit: number;
  selectedRows?: IDebitoIpva[];
  clearSelectedRows?: boolean;
  pagandoId?: string | null;
  filtros: IFiltroDebitoIpva;
  onSelectRows?: (rows: IDebitoIpva[]) => void;
  onPagarIndividual?: (id: number, exercicio: number) => void;
  onPagarManual?: (debito: IDebitoIpva) => void;
  onBaixarComprovante?: (id: number, exercicio: number) => void;
  onPagarEmLote?: () => void;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  exportarDebitos: (filtros: IFiltroDebitoIpva) => Promise<any>;
  onClickProcesso: (id: number | string) => void;
  loadingExportar: boolean;
  onSort?: (coluna: TableColumn<any>, direcao: string) => void;
}

export function ListaDebitos({
  modo,
  debitos,
  totalRows,
  limit,
  selectedRows = [],
  clearSelectedRows = false,
  pagandoId = null,
  filtros,
  onSelectRows,
  onPagarIndividual,
  onPagarManual,
  onBaixarComprovante,
  onPagarEmLote,
  onPageChange,
  onLimitChange,
  exportarDebitos,
  onClickProcesso,
  loadingExportar,
  onSort,
}: IProps) {
  const storageKey = LOCAL_STORAGE_KEY[modo];
  const defaultColunas =
    modo === "aguardando"
      ? defaultConfigColunasAguardando
      : defaultConfigColunasPago;

  const [configColunas, setConfigColunas] = useState<
    { nome: string; exibir: boolean }[]
  >(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      return stored ? JSON.parse(stored) : defaultColunas;
    } catch {
      return defaultColunas;
    }
  });
  const [showModalColunas, setShowModalColunas] = useState(false);

  function handleSaveConfigColunas(
    novaConfig: { nome: string; exibir: boolean }[],
  ) {
    localStorage.setItem(storageKey, JSON.stringify(novaConfig));
    setConfigColunas(novaConfig);
  }

  const isVisible = (nome: string) =>
    !configColunas.some((c) => c.nome === nome && !c.exibir);

  const columns: IColum<IDebitoIpva>[] = [
    {
      name: "ID",
      selector: ({ id }: IDebitoIpva) =>
        id ? (
          <span
            className="link-primary"
            style={{ cursor: "pointer" }}
            onClick={() => onClickProcesso(id)}
          >
            {id}
          </span>
        ) : (
          "-"
        ),
      width: "80px",
      sortable: true,
      sortField: "id_processo",
    },
    {
      name: <span>Status processo</span>,
      selector: ({ statusProcesso }: IDebitoIpva) => statusProcesso ?? "-",
      cell: ({ statusProcesso }: IDebitoIpva) =>
        statusProcesso != null ? (
          <StatusProcesso status={statusProcesso} />
        ) : (
          "-"
        ),
      width: "80px",
      omit: !isVisible("statusProcesso"),
    },
    {
      name: "Empresa",
      selector: ({ razaoSocial }: IDebitoIpva) => razaoSocial ?? "-",
      omit: !isVisible("empresa"),
      width: "300px",
      sortable: true,
      sortField: "razao_social",
    },
    {
      name: "Placa",
      selector: ({ placa }: IDebitoIpva) => placa ?? "-",
      width: "100px",
      omit: !isVisible("placa"),
      sortable: true,
      sortField: "placa",
    },
    {
      name: "Renavam",
      selector: ({ renavam }: IDebitoIpva) => renavam ?? "-",
      width: "130px",
      omit: !isVisible("renavam"),
      sortable: true,
      sortField: "renavam",
    },
    {
      name: "Chassi",
      selector: ({ chassi }: IDebitoIpva) => chassi ?? "-",
      width: "180px",
      omit: !isVisible("chassi"),
      sortable: true,
      sortField: "chassi",
    },
    {
      name: "Solicitação",
      selector: ({ dataSolicitacao }: IDebitoIpva) =>
        dataSolicitacao ? converterDataHoraBr(dataSolicitacao) : "-",
      width: "150px",
      omit: !isVisible("solicitacao"),
      sortable: true,
      sortField: "data_disponibilizacao_ipva",
    },
    {
      name: "Exercício",
      selector: ({ exercicio }: IDebitoIpva) => exercicio ?? "-",
      width: "100px",
      omit: !isVisible("exercicio"),
      sortable: true,
      sortField: "exercicio",
    },
    {
      name: "Vencimento",
      selector: ({ dataVencimento }: IDebitoIpva) =>
        dataVencimento ? converterData(dataVencimento.split("T")[0]) : "-",
      width: "120px",
      omit: !isVisible("vencimento"),
      sortable: true,
      sortField: "vencimento",
    },
    {
      name: "Valor",
      selector: ({ valor }: IDebitoIpva) =>
        valor != null ? formatarDinheiro(Number(valor)) : "R$ 0,00",
      width: "110px",
      omit: !isVisible("valor"),
      sortable: true,
      sortField: "valor",
    },
    {
      name: "Status",
      selector: ({ statusPagamento }: IDebitoIpva) => statusPagamento ?? "-",
      cell: ({ statusPagamento, motivoErro }: IDebitoIpva) =>
        statusPagamento != null ? (
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <StatusDebito status={statusPagamento} />
            {statusPagamento === 4 && motivoErro && (
              <IconeErroDebito motivoErro={motivoErro} />
            )}
          </span>
        ) : (
          "-"
        ),
      width: "130px",
      omit: !isVisible("status"),
    },
    {
      name: "Pagamento",
      selector: ({ dataPagamento }: IDebitoIpva) =>
        dataPagamento ? converterDataHoraBr(dataPagamento) : "-",
      width: "150px",
      omit: modo === "aguardando" || !isVisible("pagamento"),
    },
    {
      name: "Ações",
      selector: () => "",
      cell: (row: IDebitoIpva) => {
        if (modo === "pago") {
          return (
            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <FaMoneyCheckAlt
                size={14}
                style={{ cursor: "pointer", color: "#555", marginLeft: 12 }}
                title="Comprovante de pagamento"
                onClick={() => onBaixarComprovante?.(row.id, row.exercicio)}
              />
            </span>
          );
        }
        const chave = `${row.id}_${row.exercicio}`;
        const pagando = pagandoId === chave;
        const pagavel =
          row.statusPagamento === StatusPagamento.Pendente ||
          row.statusPagamento === StatusPagamento.Erro;
        return (
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {pagando ? (
              <FaSpinner
                size={14}
                style={{ animation: "spin 1s linear infinite" }}
                title="Pagando..."
              />
            ) : (
              <FaDollarSign
                size={14}
                style={{
                  cursor: pagavel ? "pointer" : "default",
                  color: pagavel ? "#555" : "#ccc",
                }}
                title="Pagar IPVA"
                onClick={
                  pagavel
                    ? () => onPagarIndividual?.(row.id, row.exercicio)
                    : undefined
                }
              />
            )}
            <FaFileInvoiceDollar
              size={16}
              style={{
                cursor: pagavel ? "pointer" : "default",
                color: pagavel ? "#555" : "#ccc",
              }}
              title="Pagamento Manual"
              onClick={pagavel ? () => onPagarManual?.(row) : undefined}
            />
          </span>
        );
      },
      width: "70px",
    },
  ];

  const onClickExportar = async () => {
    const { hasErro, data } = await exportarDebitos(filtros);
    if (hasErro) {
      toastErro(data?.message ?? "Erro ao exportar dados");
      return;
    }

    const itens: IDebitoIpva[] = data.itens ?? [];

    const col = (
      label: string,
      config: string | null,
      wch: number,
      valor: (item: IDebitoIpva) => string | number,
    ) => ({ label, config, wch, valor });

    const colunasRelatorio = [
      col("ID", null, 10, (item) => item.id ?? "-"),
      col("Empresa", "empresa", 30, (item) => item.razaoSocial ?? "-"),
      col("Exercício", "exercicio", 12, (item) => item.exercicio ?? "-"),
      col("Placa", "placa", 10, (item) => item.placa ?? "-"),
      col("Renavam", "renavam", 14, (item) => item.renavam ?? "-"),
      col("Chassi", "chassi", 22, (item) => item.chassi ?? "-"),
      col("Solicitação", "solicitacao", 14, (item) =>
        item.dataSolicitacao
          ? (converterData(item.dataSolicitacao.split("T")[0]) ?? "-")
          : "-",
      ),
      col("Vencimento", "vencimento", 14, (item) =>
        item.dataVencimento
          ? (converterData(item.dataVencimento.split("T")[0]) ?? "-")
          : "-",
      ),
      col("Valor", "valor", 14, (item) =>
        item.valor != null ? Number(item.valor) : 0,
      ),
      col("Status", "status", 16, (item) => {
        const map: Record<number, string> = {
          0: "Pendente",
          1: "Pago",
          2: "Pagamento manual",
          3: "Processando",
          4: "Erro",
        };
        return map[item.statusPagamento] ?? "-";
      }),
      col("Status processo", "statusProcesso", 18, (item) => {
        const map: Record<number, string> = {
          0: "Pré cadastro",
          1: "Em andamento",
          2: "Pendente",
          3: "Finalizado",
          4: "Cancelado",
        };
        return map[item.statusProcesso] ?? "-";
      }),
      col("Pagamento", "pagamento", 14, (item) =>
        item.dataPagamento
          ? (converterData(item.dataPagamento.split("T")[0]) ?? "-")
          : "-",
      ),
    ].filter(({ config }) => {
      if (config === null) return true;
      if (config === "pagamento" && modo !== "pago") return false;
      return isVisible(config);
    });

    const dadosPlanilha = itens.map((item) =>
      Object.fromEntries(
        colunasRelatorio.map(({ label, valor }) => [label, valor(item)]),
      ),
    );

    const planilha = utils.json_to_sheet(dadosPlanilha);
    planilha["!cols"] = colunasRelatorio.map(({ wch }) => ({ wch }));

    const valorIdx = colunasRelatorio.findIndex(
      ({ label }) => label === "Valor",
    );
    if (valorIdx >= 0) {
      const colLetter = utils.encode_col(valorIdx);
      for (let r = 1; r <= itens.length; r++) {
        const cellRef = `${colLetter}${r + 1}`;
        if (planilha[cellRef]) planilha[cellRef].z = "R$ #,##0.00";
      }
    }
    const workBook = utils.book_new();
    utils.book_append_sheet(workBook, planilha, "IPVA");

    const hoje = new Date();
    const dia = String(hoje.getDate()).padStart(2, "0");
    const mes = String(hoje.getMonth() + 1).padStart(2, "0");
    const ano = hoje.getFullYear();
    writeFileXLSX(workBook, `Pagar_IPVA_${dia}-${mes}-${ano}.xlsx`);
  };

  return (
    <Container>
      <ModalConfigColunas
        show={showModalColunas}
        handleClose={() => setShowModalColunas(false)}
        configColunas={configColunas}
        onSave={handleSaveConfigColunas}
      />
      <Header>
        <TextoHeader>Débitos</TextoHeader>
        <HeaderContainerBotoes>
          <PrimaryButton
            buttonWidth="xl"
            buttonWidthTipo="px"
            type="button"
            onClick={loadingExportar ? undefined : onClickExportar}
            outline
            variante="primary"
            style={{
              fontSize: 12,
              width: 97,
              cursor: loadingExportar ? "default" : undefined,
            }}
          >
            {loadingExportar ? (
              <span
                style={{
                  display: "inline-block",
                  transform: "scale(0.45)",
                  margin: "-0.66rem",
                }}
              >
                <LoadingCirculo mostrar={loadingExportar} />
              </span>
            ) : (
              <>
                <FaDownload /> Exportar
              </>
            )}
          </PrimaryButton>
          <PrimaryButton
            buttonWidth="xl"
            buttonWidthTipo="px"
            type="button"
            onClick={() => setShowModalColunas(true)}
            outline
            variante="primary"
            style={{ fontSize: 12 }}
          >
            <FaColumns />
            Colunas
          </PrimaryButton>
          {modo === "aguardando" && (
            <PrimaryButton
              type="button"
              onClick={onPagarEmLote}
              disabled={
                selectedRows.length < 2 ||
                selectedRows.some(
                  (r) =>
                    r.statusPagamento !== StatusPagamento.Pendente &&
                    r.statusPagamento !== StatusPagamento.Erro,
                )
              }
              buttonWidth="lg"
              buttonWidthTipo="px"
              style={{ fontSize: 12 }}
            >
              Pagar em lote
            </PrimaryButton>
          )}
        </HeaderContainerBotoes>
      </Header>
      <hr />
      <Table
        key={limit}
        keyField="uid"
        totalRows={totalRows}
        pagination={true}
        columns={columns}
        data={debitos}
        paginationRowsPerPageOptions={[30, 50, 100, 200]}
        loading={false}
        noSubHeader
        possuiTooltip={false}
        selectableRows={modo === "aguardando"}
        setRowsSelected={onSelectRows}
        clearSelectedRows={clearSelectedRows}
        headerFixo
        onChangePage={onPageChange}
        onChangeRowsPerPage={onLimitChange}
        rowsPerPage={limit}
        handleSortFunction={onSort}
      />
    </Container>
  );
}
